/* eslint-disable unicorn/explicit-length-check */
/* eslint-disable unicorn/no-negated-condition */
/* eslint-disable @typescript-eslint/no-unused-expressions */
import type { Task } from "redux-saga";
import {
  call,
  cancel,
  fork,
  take,
  takeEvery,
  put,
  select,
} from "redux-saga/effects";
import moment from "moment";
import "moment-timezone";
import { DATE_FORMAT_MOMENT } from "../constants/common";
import {
  CHECK_INSTAGRAM_POST_OVERLAPPING,
  CHECK_INSTAGRAM_PREFLIGHT,
} from "../constants/ActionTypes";
import {
  checkOverlappingPosts,
  preflightInstagramPost,
  type IgPreflightResult,
} from "../api/post";
import { handleError } from "../utils/handleError";
import { getUser } from "../selectors/commonSelectors";
import type { ScheduleConflictKind } from "../components/Posts/PostForm/types";
import { AccountType } from "../shared/types/accounts";
import { isNewPostId } from "../utils/posts";
import { adminPanelFeatureToggle } from "../utils/feature-toggle-utils";
interface OverlappingResponseData {
  accountName: string;
  overlappingPostId: string;
  nextRunAt: Date;
  conflict?: ScheduleConflictKind | null;
}

interface OverlappingPost {
  type?: string | null;
  storyType?: string | null;
  storyUrl?: unknown;
  storyLabel?: unknown;
  storyStickerHeight?: unknown;
  publishStoryManually?: boolean | null;
  firstcomment?: string | null;
  mediaFiles?: unknown[] | null;
  postOptions?: { isNotification?: boolean | null } | null;
}

interface PreflightPayload {
  payload: {
    postId: string;
    accounts: AccountType[];
    post?: unknown;
  };
}

interface OverlappingPayload {
  payload: {
    postId: string;
    accounts: AccountType[];
    isNow: boolean;
    when: string;
    post?: OverlappingPost;
    done: () => Promise<void>;
  };
}

/**
 * The facts the server needs to work out how this post will publish.
 *
 * Raw facts only. Whether they add up to a post that skips the 15-minute
 * spacing rule is the server's decision, and duplicating it here would put two
 * copies of the rule in the product.
 */
function trackFacts(post: OverlappingPost | undefined) {
  if (!post) return {};

  return {
    postType: post.type ?? undefined,
    storyType: post.storyType ?? undefined,
    publishStoryManually: post.publishStoryManually === true,
    isNotification: post.postOptions?.isNotification === true,
    hasStorySticker: Boolean(
      post.storyUrl || post.storyLabel || post.storyStickerHeight
    ),
    hasFirstComment:
      typeof post.firstcomment === "string" &&
      post.firstcomment.trim().length > 0,
    mediaFileCount: Array.isArray(post.mediaFiles) ? post.mediaFiles.length : 0,
  };
}

export function* checkInstagramPostOverlapping({
  payload,
}: OverlappingPayload): Generator {
  const { postId, accounts, isNow, when, post, done } = payload;

  if (!(isNow || when)) {
    done && done();

    return;
  }

  /*
   * Every platform, not only Instagram. This used to filter to "IG" and
   * synthesise a no-overlap result for everything else, while the server's edit
   * and draft paths bound every platform. A LinkedIn customer could therefore
   * create two posts five minutes apart and then be blocked editing either,
   * with no warning at the point the clash was made. SKE-4566.
   */
  if (!accounts.length) {
    yield put(
      CHECK_INSTAGRAM_POST_OVERLAPPING.success({
        postId,
        data: {
          accountName: null,
          overlappingPostId: null,
          nextRunAt: null,
        },
      })
    );
    done && done();

    return;
  }

  try {
    yield put(CHECK_INSTAGRAM_POST_OVERLAPPING.request());
    const user = yield select(getUser);
    const checkMoment = isNow
      ? moment(new Date()).tz(user.timezone)
      : moment(when);
    // Don't send the literal "Invalid date" string to the overlap check;
    // treat an unparseable date as "no overlap".
    if (!checkMoment.isValid()) {
      yield put(
        CHECK_INSTAGRAM_POST_OVERLAPPING.success({
          postId,
          data: {
            accountName: null,
            overlappingPostId: null,
            nextRunAt: null,
          },
        })
      );
      done && done();
      return;
    }
    const data: OverlappingResponseData = yield call(checkOverlappingPosts, {
      accountIds: accounts.map(({ _id }) => _id),
      date: checkMoment.format(DATE_FORMAT_MOMENT),
      postId: !isNewPostId(postId) ? postId : undefined,
      ...trackFacts(post),
    });
    yield put(CHECK_INSTAGRAM_POST_OVERLAPPING.success({ postId, data }));
    // eslint-disable-next-line unicorn/catch-error-name
  } catch (err) {
    yield put(CHECK_INSTAGRAM_POST_OVERLAPPING.failure({ postId }));
    handleError(err, "Instagram available time check failed");
  } finally {
    done && done();
  }
}

/**
 * The reconnect sentence behind a 403, and nothing else.
 *
 * The preflight route refuses the WHOLE request when any selected account has
 * no usable Instagram token, so one expired account hides the findings for
 * every healthy one and this sentence, naming the account, is the only thing
 * left for the customer to act on. Other failures carry nothing they can use,
 * and "we could not check" says more than an internal error string would.
 */
function reconnectMessage(error: unknown): string | undefined {
  const response = (
    error as {
      response?: { status?: number; data?: { message?: unknown } };
    }
  )?.response;

  if (response?.status !== 403) return undefined;

  const message = response.data?.message;
  return typeof message === "string" && message.trim() ? message : undefined;
}

/**
 * Asks the server how this draft would publish, per selected account.
 *
 * SKE-4567. The composer needs this to know whether a story chain is capped at
 * three items: three is a PHONE limit, because a device publishes a story set as
 * one message with one outcome, and an API-track chain has neither property.
 *
 * The whole post goes to the server deliberately. Capability needs the account's
 * scopes and business-account id, which the accounts payload does not carry, and
 * re-deriving the expressibility rules here would put a second copy of them in
 * the product.
 *
 * A failure never blocks a save. It records the same "unavailable" answer the
 * server gives when its own preflight throws, which carries no accounts and so
 * keeps every phone-era limit in force. That is the safe direction: the worst
 * outcome is a customer held to a limit that no longer applies to them, rather
 * than one offered a chain the publish path would refuse.
 */
export function* checkInstagramPreflight({
  payload,
}: PreflightPayload): Generator {
  const { postId, accounts, post } = payload;

  /*
   * SKE-4733. Only a workspace on the Instagram API track asks. Without the
   * gate every Instagram account publishes from the phone, so the answer would
   * only ever add phone-track notices and lift nothing, and the composer's
   * phone-era limits (the story chain cap on auto stories) apply as they did.
   * Waits for toggles on a cold load, so an enabled workspace that opens the
   * composer before they arrive still gets its preflight; until then no
   * request goes out.
   */
  const apiPublishingOn = (yield call(() =>
    adminPanelFeatureToggle.get("instagramApiPublishing")
  )) as boolean;
  if (!apiPublishingOn) return;

  const instagramAccounts = accounts.filter(
    (account) => (account.platformType ?? "IG") === "IG"
  );

  if (!instagramAccounts.length || !post) return;

  try {
    const data: IgPreflightResult = yield call(preflightInstagramPost, {
      accountIds: instagramAccounts.map(({ _id }) => _id),
      post,
    });

    yield put(CHECK_INSTAGRAM_PREFLIGHT.success({ postId, data }));
  } catch (error) {
    // The routine's creator takes one argument, so an id sent as `meta` never
    // reaches the reducer.
    yield put(
      CHECK_INSTAGRAM_PREFLIGHT.failure({
        postId,
        message: reconnectMessage(error),
      })
    );
  }
}

export function* postFormSagas(): Generator {
  yield takeEvery(
    CHECK_INSTAGRAM_POST_OVERLAPPING.TRIGGER,
    checkInstagramPostOverlapping
  );
  yield fork(latestInstagramPreflightPerPost);
}

// One saga serves every open form, so a newer request cancels only its own post's older one.
function* latestInstagramPreflightPerPost(): Generator {
  const running = new Map<string, Task>();
  while (true) {
    const action = (yield take(
      CHECK_INSTAGRAM_PREFLIGHT.TRIGGER
    )) as PreflightPayload;
    const { postId } = action.payload;
    for (const [id, task] of running) {
      if (!task.isRunning()) running.delete(id);
    }
    const previous = running.get(postId);
    if (previous) yield cancel(previous);
    running.set(postId, (yield fork(checkInstagramPreflight, action)) as Task);
  }
}
